import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../lib/auth";
import { motion } from "framer-motion";
import { List, X, SignOut, User, CaretDown } from "@phosphor-icons/react";
import LanguageToggle from "./LanguageToggle";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "./ui/dropdown-menu";

export default function Navbar({ variant = "marketing" }) {
    const { user, logout } = useAuth();
    const { t } = useTranslation();
    const nav = useNavigate();
    const loc = useLocation();
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    // Close mobile menu when the route changes / when escape is pressed
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
        document.body.style.overflow = "hidden";
        window.addEventListener("keydown", onKey);
        return () => { document.body.style.overflow = ""; window.removeEventListener("keydown", onKey); };
    }, [open]);

    const isLanding = variant === "marketing";
    const transparent = isLanding && !scrolled;

    // Smart click handler for in-page anchors (#features, #how, #pricing).
    // If we're already on "/" → smooth-scroll to the target section.
    // If we're on another route → navigate to "/<anchor>" so the browser jumps.
    const handleAnchorClick = (anchor) => (e) => {
        const id = anchor.replace(/^#/, "");
        if (loc.pathname === "/") {
            e.preventDefault();
            const el = document.getElementById(id);
            if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "start" });
                window.history.replaceState(null, "", "/" + anchor);
            }
        }
        // Otherwise let the default <a href="/#foo"> behaviour run after route change.
        setOpen(false);
    };

    // Logo click — if already on "/", scroll to top instead of doing nothing.
    const handleLogoClick = (e) => {
        if (loc.pathname === "/") {
            e.preventDefault();
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
        setOpen(false);
    };

    const linkClass = `text-sm font-semibold uppercase tracking-[0.18em] whitespace-nowrap transition-colors ${
        transparent ? "text-white/90 hover:text-accent" : "text-primary hover:text-accent"
    }`;

    return (
        <motion.header
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className={`fixed top-0 inset-x-0 z-40 transition-all duration-300 ${
                transparent ? "bg-transparent" : "bg-background/90 backdrop-blur-xl border-b border-border"
            }`}
            data-testid="main-navbar"
            data-no-translate
        >
            <div className="px-4 sm:px-6 md:px-8 lg:px-12 xl:px-24 h-16 sm:h-20 flex items-center justify-between gap-3">
                <Link to="/" onClick={handleLogoClick} className="flex items-center gap-2 sm:gap-3 group shrink-0" data-testid="brand-logo">
                    <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-md flex items-center justify-center shadow-sm transition-colors ${transparent ? "bg-accent text-primary ring-1 ring-white/30" : "bg-primary text-primary-foreground"}`}>
                        <span className="font-heading text-lg sm:text-xl font-bold leading-none">cf</span>
                    </div>
                    <div className="flex flex-col leading-none">
                        <span className={`font-heading text-base sm:text-xl tracking-tight transition-colors ${transparent ? "text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]" : "text-primary"}`}>CharityFundraiser</span>
                        <span className={`label-eyebrow text-[9px] sm:text-[10px] mt-0.5 sm:mt-1 transition-colors hidden sm:block ${transparent ? "text-accent drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]" : ""}`}>Elite Fundraising Platform</span>
                    </div>
                </Link>

                {/* Desktop nav — primary links inline, secondary tucked into a "More" menu
                    so the row never overflows (even in ES with longer labels). */}
                <nav className="hidden lg:flex items-center gap-5 xl:gap-7 ml-auto">
                    {isLanding && (
                        <>
                            <Link to="/features" className={linkClass} data-testid="nav-features">{t("nav.features")}</Link>
                            <Link to="/problems-we-solve" className={linkClass} data-testid="nav-problems">{t("nav.whyUs")}</Link>
                            <a href={loc.pathname === "/" ? "#how" : "/#how"} onClick={handleAnchorClick("#how")} className={linkClass} data-testid="nav-how">{t("nav.howItWorks")}</a>
                            <Link to="/demo" className={linkClass} data-testid="nav-demo">{t("nav.tryDemo")}</Link>
                            <a href={loc.pathname === "/" ? "#pricing" : "/#pricing"} onClick={handleAnchorClick("#pricing")} className={linkClass} data-testid="nav-pricing">{t("nav.pricing")}</a>

                            <DropdownMenu>
                                <DropdownMenuTrigger className={`${linkClass} inline-flex items-center gap-1 outline-none`} data-testid="nav-more">
                                    {t("nav.more")} <CaretDown size={12} weight="bold" />
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-56" data-testid="nav-more-menu">
                                    <DropdownMenuItem asChild>
                                        <Link to="/requests" data-testid="nav-requests">{t("nav.roadmap")}</Link>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem asChild>
                                        <Link to="/tour" data-testid="nav-tour">{t("nav.liveTour")}</Link>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem asChild>
                                        <Link to="/find-an-auctioneer" className="flex items-center justify-between gap-2" data-testid="nav-find-auctioneer">
                                            <span>{t("nav.findAuctioneers")}</span>
                                            <span className="text-[10px] bg-accent text-accent-foreground px-1.5 py-0.5 font-bold tracking-wider">8,800+</span>
                                        </Link>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem asChild>
                                        <Link to="/compare" data-testid="nav-compare">{t("nav.compare")}</Link>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem asChild>
                                        <Link to="/examples" data-testid="nav-examples">{t("nav.examples")}</Link>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem asChild>
                                        <Link to="/faqs" data-testid="nav-faqs">{t("nav.faqs")}</Link>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem asChild>
                                        <Link to="/blog" data-testid="nav-blog">{t("nav.news")}</Link>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem asChild>
                                        <Link to="/about" data-testid="nav-about">{t("nav.about")}</Link>
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </>
                    )}
                </nav>

                <div className="hidden lg:flex items-center gap-4 shrink-0">
                    <LanguageToggle className={transparent ? "text-white" : "text-primary"} />
                    {user ? (
                        <>
                            <Link
                                to={user.role === "super_admin" ? "/super" : user.role === "org_admin" ? "/admin" : "/dashboard"}
                                className={`${linkClass} flex items-center gap-2`}
                                data-testid="nav-dashboard"
                            >
                                <User size={16} weight="regular" />
                                {user.name.split(" ")[0]}
                            </Link>
                            <button onClick={() => { logout(); nav("/"); }} className={`btn-ghost ${transparent ? "text-white hover:bg-white/10" : ""}`} data-testid="nav-logout">
                                <SignOut size={14} /> {t("nav.logout")}
                            </button>
                        </>
                    ) : (
                        <>
                            <Link to="/login" className={linkClass} data-testid="nav-login">{t("nav.signIn")}</Link>
                            <Link to="/signup-org" className="btn-accent whitespace-nowrap" data-testid="nav-cta">{t("nav.startFundraising")}</Link>
                        </>
                    )}
                </div>

                {/* Hamburger — visible below lg (so tablet + mobile both get it) */}
                <button
                    className={`lg:hidden p-2 rounded-md transition-colors ${transparent ? "text-white hover:bg-white/10" : "text-primary hover:bg-primary/10"}`}
                    onClick={() => setOpen(!open)}
                    aria-label={open ? "Close menu" : "Open menu"}
                    data-testid="mobile-menu-toggle"
                >
                    {open ? <X size={26} weight="bold" /> : <List size={26} weight="bold" />}
                </button>
            </div>

            {/* Mobile + tablet drawer */}
            {open && (
                <div
                    className="lg:hidden bg-background border-t border-border px-6 py-6 flex flex-col gap-1 shadow-xl max-h-[calc(100vh-4rem)] overflow-y-auto"
                    data-testid="mobile-menu"
                >
                    {isLanding && (
                        <>
                            <Link to="/features" className="block py-3 label-eyebrow border-b border-border" onClick={() => setOpen(false)}>{t("nav.features")}</Link>
                            <Link to="/problems-we-solve" className="block py-3 label-eyebrow border-b border-border" onClick={() => setOpen(false)} data-testid="mobile-nav-problems">{t("nav.whyUs")}</Link>
                            <Link to="/requests" className="block py-3 label-eyebrow border-b border-border" onClick={() => setOpen(false)} data-testid="mobile-nav-requests">{t("nav.roadmap")}</Link>
                            <a href={loc.pathname === "/" ? "#how" : "/#how"} onClick={handleAnchorClick("#how")} className="block py-3 label-eyebrow border-b border-border">{t("nav.howItWorks")}</a>
                            <Link to="/tour" className="block py-3 label-eyebrow border-b border-border" onClick={() => setOpen(false)}>{t("nav.liveTour")}</Link>
                            <Link to="/find-an-auctioneer" className="block py-3 label-eyebrow border-b border-border" onClick={() => setOpen(false)} data-testid="mobile-nav-find-auctioneer">
                                {t("nav.findAuctioneers")}
                                <span className="ml-2 text-[10px] bg-accent text-accent-foreground px-1.5 py-0.5 font-bold tracking-wider">8,800+</span>
                            </Link>
                            <Link to="/compare" className="block py-3 label-eyebrow border-b border-border" onClick={() => setOpen(false)} data-testid="mobile-nav-compare">{t("nav.compare")}</Link>
                            <Link to="/examples" className="block py-3 label-eyebrow border-b border-border" onClick={() => setOpen(false)} data-testid="mobile-nav-examples">{t("nav.examples")}</Link>
                            <Link to="/faqs" className="block py-3 label-eyebrow border-b border-border" onClick={() => setOpen(false)} data-testid="mobile-nav-faqs">{t("nav.faqs")}</Link>
                            <Link to="/blog" className="block py-3 label-eyebrow border-b border-border" onClick={() => setOpen(false)}>{t("nav.news")}</Link>
                            <Link to="/about" className="block py-3 label-eyebrow border-b border-border" onClick={() => setOpen(false)} data-testid="mobile-nav-about">{t("nav.about")}</Link>
                            <a href={loc.pathname === "/" ? "#pricing" : "/#pricing"} onClick={handleAnchorClick("#pricing")} className="block py-3 label-eyebrow border-b border-border">{t("nav.pricing")}</a>
                            <Link to="/contact" className="block py-3 label-eyebrow border-b border-border" onClick={() => setOpen(false)}>{t("nav.contact")}</Link>
                        </>
                    )}
                    <div className="mt-4 flex flex-col gap-3">
                        <div className="flex items-center justify-center pb-2"><LanguageToggle /></div>
                        {user ? (
                            <>
                                <Link to={user.role === "super_admin" ? "/super" : user.role === "org_admin" ? "/admin" : "/dashboard"} className="btn-ghost text-center" onClick={() => setOpen(false)}>{t("nav.dashboard")}</Link>
                                <button onClick={() => { logout(); setOpen(false); nav("/"); }} className="btn-ghost text-center">{t("nav.logout")}</button>
                            </>
                        ) : (
                            <>
                                <Link to="/login" className="btn-ghost text-center" onClick={() => setOpen(false)}>{t("nav.signIn")}</Link>
                                <Link to="/signup-org" className="btn-accent text-center" onClick={() => setOpen(false)}>{t("nav.startFundraising")}</Link>
                            </>
                        )}
                    </div>
                </div>
            )}
        </motion.header>
    );
}
